import Button from '@ben/orion/button';
import Menu from '@ben/orion/menu';
import Switch from '@ben/orion/switch';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { CustomComponentTypes } from '@ben/shared/constants';
import type {
  IModularBenefitDetail,
  LifeEventRestrictionComponent,
} from '@ben/shared/types/columbia';
import { useDisclosure } from '@mantine/hooks';
import { filter } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';
import RestrictionGroup from '../restriction-set/restriction-group';
import {
  RESTRICTION_TYPE_KEYS,
  type RestrictionType,
} from '../restriction-set/types';
import {
  RESTRICTION_CONFIG,
  useRestrictionFieldArrays,
} from '../restriction-set/use-restriction-field-arrays';
import DisabledRestrictionSet from './disabled-restriction-set';
import useLifeEventRestrictionForm from './hooks/use-life-event-restriction-form';

const RESTRICTION_SET_OPTIONS: {
  label: string;
  value: RestrictionType;
}[] = [
  { label: 'Frequency limits', value: 'frequencyLimits' },
  { label: 'Step size limits', value: 'stepSizeLimits' },
  { label: 'Total limits', value: 'totalLimits' },
  { label: 'Life event limits', value: 'lifeEventLimits' },
  { label: 'Formula based restrictions', value: 'formulaBasedRestrictions' },
  { label: 'Exclusion periods', value: 'exclusionPeriods' },
  {
    label: 'Conditional contact age restrictions',
    value: 'conditionalContactAgeRestrictions',
  },
];

const COMPONENTS_WITH_FULL_RESTRICTIONS = new Set([
  CustomComponentTypes.DROPDOWN,
  CustomComponentTypes.AMOUNT_INPUT,
  CustomComponentTypes.PENSION_SLIDERS,
  CustomComponentTypes.SLIDER,
  CustomComponentTypes.BUY_SELL_HOLIDAY,
]);

const isRestrictionSupported = (
  componentType: CustomComponentTypes,
  restrictionType: RestrictionType
): boolean => {
  if (restrictionType === 'conditionalContactAgeRestrictions') {
    return componentType === CustomComponentTypes.CONTACTS;
  }

  if (
    restrictionType === 'formulaBasedRestrictions' ||
    restrictionType === 'exclusionPeriods'
  ) {
    return true;
  }
  return COMPONENTS_WITH_FULL_RESTRICTIONS.has(componentType);
};

interface ParentComponentData {
  componentId: number;
  type: CustomComponentTypes;
  name: string;
}

interface RestrictionComponentProps {
  component: LifeEventRestrictionComponent;
  lifeEventIndex: number;
  stepId: number;
  parentComponent?: ParentComponentData;
}

const RestrictionComponent = ({
  component,
  lifeEventIndex,
  stepId,
  parentComponent,
}: RestrictionComponentProps) => {
  const [opened, { toggle, close }] = useDisclosure(false);

  const { control, setValue, trigger } =
    useFormContext<IModularBenefitDetail>();

  const {
    restrictionSetPath,
    restrictionAppliedPath,
    componentExistsInForm,
    upsertComponent,
    updateRestrictionApplied,
  } = useLifeEventRestrictionForm({
    lifeEventIndex,
    stepId,
    component: {
      componentId: component.componentId,
      type: component.type,
      name: component.name,
    },
    parentComponent,
    autoRegister: true,
  });

  const formRestrictionApplied = useWatch({
    name: restrictionAppliedPath,
    control,
  });

  const isRestrictionEnabled =
    typeof formRestrictionApplied === 'boolean' ? formRestrictionApplied : true;

  const handleRestrictionToggle = useCallback(
    (enabled: boolean) => {
      updateRestrictionApplied(enabled);
    },
    [updateRestrictionApplied]
  );

  const existingRestrictionSet = component.restrictionSet;

  const availableOptions = useMemo(() => {
    return RESTRICTION_SET_OPTIONS.filter((option) =>
      isRestrictionSupported(component.type, option.value)
    );
  }, [component.type]);

  // Only use the actual path when the component exists in the form
  // Using a non-existent dummy path prevents useFieldArray from creating phantom entries
  const safeRestrictionSetPath = componentExistsInForm
    ? restrictionSetPath
    : '__dummy_path_that_will_not_be_used__';

  const fieldArrays = useRestrictionFieldArrays(
    control,
    safeRestrictionSetPath
  );

  const [openAccordions, setOpenAccordions] = useState<string[]>(() =>
    filter(RESTRICTION_TYPE_KEYS, (type) => fieldArrays[type].fields.length > 0)
  );

  const handleRestrictionSelect = useCallback(
    (restrictionType: RestrictionType) => {
      upsertComponent();

      const config = RESTRICTION_CONFIG[restrictionType];
      fieldArrays[restrictionType].append(config.defaultValues);

      if (!openAccordions.includes(restrictionType)) {
        setOpenAccordions((previous) => [...previous, restrictionType]);
      }

      close();
    },
    [close, fieldArrays, openAccordions, upsertComponent]
  );

  const handleAccordionToggle = useCallback(
    (accordionValue: string) => (isOpen: boolean) => {
      setOpenAccordions((previous) => {
        if (isOpen) {
          return previous.includes(accordionValue)
            ? previous
            : [...previous, accordionValue];
        }
        return previous.filter((value) => value !== accordionValue);
      });
    },
    []
  );

  const hasExistingRestrictions = existingRestrictionSet
    ? RESTRICTION_TYPE_KEYS.some(
        (key) => (existingRestrictionSet[key]?.length ?? 0) > 0
      )
    : false;

  const hasNewRestrictions = useMemo(() => {
    return Object.values(fieldArrays).some(
      (restrictionArray) => restrictionArray.fields.length > 0
    );
  }, [fieldArrays]);

  const hasAnyRestrictions = hasExistingRestrictions || hasNewRestrictions;

  const hintText = isRestrictionEnabled
    ? 'When enabled, employees will be able to make changes to this component.'
    : 'When disabled, employees won’t be able to make changes to this component.';

  return (
    <Wrapper>
      <Header>
        <HeaderText>
          <Typography variant={TypographyVariant.bodyLg3}>
            {component.type.toUpperCase()}: ({component.name})
          </Typography>
          <Switch
            label={isRestrictionEnabled ? 'Enabled' : 'Disabled'}
            defaultValue={isRestrictionEnabled}
            onChange={handleRestrictionToggle}
            hintText={hintText}
          />
        </HeaderText>
        {availableOptions.length > 0 && (
          <Menu
            position="bottom-end"
            opened={opened}
            onChange={toggle}
            closeOnItemClick={false}
          >
            <Menu.Target>
              <Button
                variant="text"
                aria-expanded={opened}
                disabled={!isRestrictionEnabled}
              >
                Add restriction
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {availableOptions.map((option) => (
                <Menu.Item
                  key={option.value}
                  onClick={() => handleRestrictionSelect(option.value)}
                >
                  {option.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        )}
      </Header>

      {hasExistingRestrictions && existingRestrictionSet && (
        <DisabledRestrictionSet
          restrictionSet={existingRestrictionSet}
          availableOptions={availableOptions}
        />
      )}

      {hasNewRestrictions &&
        availableOptions.map((option) => {
          const type = option.value;
          const { fields, remove } = fieldArrays[type];

          if (fields.length === 0) {
            return null;
          }

          const {
            component: FormComponent,
            title,
            needsSetValue,
            needsTrigger,
          } = RESTRICTION_CONFIG[type];

          return (
            <RestrictionGroup
              key={type}
              title={title}
              isOpen={openAccordions.includes(type)}
              onToggle={handleAccordionToggle(type)}
            >
              {fields.map((field, index) => (
                <FormComponent
                  key={field.id}
                  index={index}
                  control={control}
                  restrictionSetPath={restrictionSetPath}
                  onRemove={() => remove(index)}
                  {...(needsSetValue ? { setValue } : {})}
                  {...(needsTrigger ? { trigger } : {})}
                />
              ))}
            </RestrictionGroup>
          );
        })}

      {!hasAnyRestrictions && (
        <Typography variant={TypographyVariant.bodyMd1}>
          No restrictions to show
        </Typography>
      )}

      {component.subComponents && component.subComponents.length > 0 && (
        <SubComponentsContainer>
          {component.subComponents.map((subComponent) => (
            <RestrictionComponent
              key={subComponent.componentId}
              component={subComponent}
              lifeEventIndex={lifeEventIndex}
              stepId={stepId}
              parentComponent={{
                componentId: component.componentId,
                type: component.type,
                name: component.name,
              }}
            />
          ))}
        </SubComponentsContainer>
      )}
    </Wrapper>
  );
};

export default RestrictionComponent;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    width: 100%;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(3)};
    border-radius: ${theme.borderRadius.md};
    background: ${theme.palette.neutral[10]};
    border: 1px solid ${theme.palette.primary.main};
  `};
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const HeaderText = styled(Typography)`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
    align-items: center;
  `};
`;

const SubComponentsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    padding-left: ${theme.spacing(3)};
  `};
`;
